import { useState, useContext, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    CloudDownload as CloudDownloadIcon,
    ShoppingCart as ShoppingCartIcon,
    Lock as LockIcon,
    LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
    AppContext,
    SttTopControls,
    SttTable,
    SttChipGroup,
    SttButtonFab,
    SttPage,
} from '../../../sporttia/all';
import CreateGroupDialog from './CreateGroupDialog';
import { useCrud, useToggle, useTuples } from '../../../lib/hooks';
import Group from './Group';
import DiscountTooltip from '../../../components/discounts/DiscountTooltip';
import type { Discount } from '../../../types/models/Discount';
import { sporttiaColors } from '../../../theme/theme';
import { SttHasDeviceIcon } from '../../../sporttia/icons/SttHasDeviceIcon';
import { useGroupsTour } from 'src/lib/tours/useGroupsTour';
import { PERMISSIONS } from '../../../lib/permissions';

type GroupItem = {
    id: number;
    name: string;
    type: string;
    created?: string;
    enrollmentPrice?: number;
    feePrice?: number;
    feeInterval?: string;
    discounts?: Discount[];
    ini?: string;
    end?: string;
    purchaseOnline?: boolean;
    numActiveUsers?: number;
    maxMembers?: number;
    mandatoryGroup?: { id: number; name: string };
    hasDevice?: boolean;
    trash?: boolean;
};

type GroupState = {
    id: number | null;
    trash: boolean;
};

// Users & Groups screen
export default function Groups(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const { startTour } = useGroupsTour();
    const { id: urlGroupId, tab: urlTabKey } = useParams<{
        id?: string;
        tab?: string;
    }>();

    // Set page header title
    useEffect(() => {
        cxt.setHeader({ title: t('GroupsAndAbonados') });
    }, []);

    const [groups, setGroups, , Get] = useCrud<{
        count?: number;
        rows?: GroupItem[];
    }>({});
    const [group, setGroup] = useState<GroupState>(() => ({
        id: urlGroupId ? parseInt(urlGroupId, 10) || null : null,
        trash: false,
    }));
    const [filters, setFilter] = useTuples({
        name: '',
        year: new Date().getFullYear(),
        inTime: true,
        types: 'NORMAL,ABONADOS,FAMILY',
    });

    const [dialog, setDialog] = useState(false);

    const [trash, toggleTrash] = useToggle(() => {
        loadGroups();
    });

    // Handle URL parameter changes
    useEffect(() => {
        if (urlGroupId) {
            const parsedId = parseInt(urlGroupId, 10);
            if (!Number.isNaN(parsedId)) {
                setGroup((prev) => ({
                    id: parsedId,
                    trash: prev.id === parsedId ? prev.trash : false,
                }));
            }
        }
    }, [urlGroupId]);

    /**
     * Load groups
     */
    function loadGroups(params = { rows: 20, page: 1 }) {
        const { inTime, ...restFilters } = filters;
        const formattedFilters = inTime ? filters : restFilters;

        Get<any>(`/scs/${cxt?.sc?.id}/groups`, {
            ...params,
            ...formattedFilters,
            trash: !!trash,
            includeChildren: true,
        }).then((response: unknown) => {
            const data = response as { count?: number; rows?: GroupItem[] };
            if (data.rows) {
                setGroups(data);
            }
        });
    }

    return (
        <SttPage title={t('GroupsAndAbonados')} help={startTour}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        type: 'text',
                        caption: t('Name'),
                        name: 'name',
                        value: filters.name,
                        onEnterKey: () => loadGroups(),
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        type: 'year',
                        name: 'year',
                        caption: t('Year'),
                        value: filters.year,
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        years: {
                            start: new Date().getFullYear(),
                            past: 5,
                            future: 3,
                        },
                    },
                    {
                        type: 'switch',
                        name: 'inTime',
                        caption: t('InForce'),
                        checked: filters.inTime,
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                    },
                    {
                        caption: t('Type'),
                        name: 'types',
                        type: 'selectMulti',
                        value: filters.types,
                        xs: 12,
                        sm: 6,
                        md: 12,
                        lg: 12,
                        options: [
                            { label: t('GT.NORMAL'), value: 'NORMAL' },
                            { label: t('GT.SPORT'), value: 'SPORT' },
                            { label: t('GT.ABONADOS'), value: 'ABONADOS' },
                            { label: t('GT.FAMILY'), value: 'FAMILY' },
                        ],
                    },
                ]}
                menuIcon={<CloudDownloadIcon />}
                onChange={({ name, value }) =>
                    setFilter({
                        name: name as keyof typeof filters,
                        value: value as string | number | boolean,
                    })
                }
                onFilter={() => loadGroups()}
                // TODO: enable trash switch when API supports querying deleted groups (and its users etc)
                onToggleTrash={toggleTrash}
            />

            {/* --- LIST --- */}
            <SttTable
                childrenEnabled
                data={groups}
                loading={false}
                columns={[
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        width: 300,
                        onClick: (row: GroupItem) =>
                            setGroup({ id: row.id, trash: row.trash || false }),
                    },
                    {
                        title: t('Type'),
                        width: 125,
                        value: (row: GroupItem) => (
                            <SttChipGroup type={row.type} />
                        ),
                    },
                    {
                        title: t('Status'),
                        value: (row: GroupItem) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Tooltip title={t('OnlinePurchase')}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: row.purchaseOnline
                                                ? alpha(
                                                      sporttiaColors.info.main,
                                                      0.15,
                                                  )
                                                : sporttiaColors.icon
                                                      .disabledBg,
                                        }}
                                    >
                                        <ShoppingCartIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.purchaseOnline
                                                    ? 'info.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        row.mandatoryGroup
                                            ? `${t('generic.mandatoryGroup')}: ${row.mandatoryGroup.name}`
                                            : t('generic.mandatoryGroup')
                                    }
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: row.mandatoryGroup
                                                ? alpha(
                                                      sporttiaColors.warning
                                                          .main,
                                                      0.15,
                                                  )
                                                : sporttiaColors.icon
                                                      .disabledBg,
                                        }}
                                    >
                                        <LockIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.mandatoryGroup
                                                    ? 'warning.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <DiscountTooltip
                                    discounts={row.discounts || []}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                row.discounts &&
                                                row.discounts.length > 0
                                                    ? alpha(
                                                          sporttiaColors.success
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                        }}
                                    >
                                        <LocalOfferIcon
                                            sx={{
                                                fontSize: 18,
                                                color:
                                                    row.discounts &&
                                                    row.discounts.length > 0
                                                        ? 'success.main'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                            }}
                                        />
                                    </Box>
                                </DiscountTooltip>
                                <SttHasDeviceIcon hasDevice={row.hasDevice} />
                            </Box>
                        ),
                    },
                    {
                        title: t('Period'),
                        type: 'period',
                        value: (row: GroupItem) => ({
                            ini: row.ini,
                            end: row.end,
                        }),
                    },
                    {
                        title: t('Fee'),
                        type: 'enrollment',
                        value: (row: GroupItem) => ({
                            enrollment: row.enrollmentPrice,
                            fee: row.feePrice,
                            interval: row.feeInterval,
                        }),
                    },
                    {
                        title: t('Members'),
                        value: (row: GroupItem) => (
                            <>
                                {row.numActiveUsers || 0} /{' '}
                                {row.maxMembers || '∞'}
                            </>
                        ),
                    },
                ]}
                onFetch={loadGroups}
            />
            {group.id && (
                <Group
                    id={group.id}
                    trash={group.trash}
                    initialTabKey={urlTabKey}
                    onChange={() => loadGroups()}
                    onClose={() => {
                        setGroup({ id: null, trash: false });
                        navigate('/groups');
                    }}
                />
            )}
            <CreateGroupDialog
                open={dialog}
                onSave={() => loadGroups()}
                onClose={() => setDialog(false)}
            />
            <SttButtonFab
                onClick={() => setDialog(true)}
                permission={PERMISSIONS.GROUP.CREATE}
            />
        </SttPage>
    );
}
